import { AddCircleRounded } from "@mui/icons-material";
import { Checkbox, Grid, Icon, IconButton, Stack, Switch } from "@mui/material";
import { makeStyles } from "@mui/styles";
import DataList from "components/DataList";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import NotificationRecipientsModal from "layouts/pages/companyjobs/components/NotificationRecipientsModal";
import Attachments from "layouts/pages/interviews/components/InterviewAttachments/Attachments";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useQueryClient } from "react-query";
import { INTERVIEW_ENTITIES } from "utils/constants/entityOptions";

const useStyle = makeStyles({
  editor: {
    height: 150,
  },
  box: {
    overflow: "visible",
    height: 550,
  },
});

const { INTERVIEW_TYPES, STATES, INTERVIEW_STATUS } = INTERVIEW_ENTITIES;

const SettingsStep = ({ clearErrors, setValue, control, getValues, errors, trigger, watch }) => {
  const { company, currentLoggedUser } = useAppContextController();
  const queryClient = useQueryClient();
  const classes = useStyle();
  const { enqueueSnackbar } = useSnackbar();
  const [notificationRecipientsModalOpen, setNotificationRecipientsModalOpen] = useState(false);

  useWatch({
    control,
  });

  const addNotificationRecipients = () => setNotificationRecipientsModalOpen(true);

  const removeRecipient = (idx) => {
    const temporaryRecipients = getValues()?.notificationRecipients;
    temporaryRecipients.splice(idx, 1);
    setValue("notificationRecipients", temporaryRecipients);
  };

  const notificationRecipientsRenderRow = (row, idx) => {
    return (
      <>
        <Stack
          display="flex"
          direction="row"
          justifyContent="space-between"
          onClick={() => {
            setNotificationRecipientsModalOpen(true);
          }}
        >
          <Grid container>
            <Grid item sm={5.5}>
              <MDTypography sx={{ fontSize: "0.9rem" }}>{row.fullName}</MDTypography>
            </Grid>

            <Grid item sm={5.5}>
              <MDTypography sx={{ fontSize: "0.9rem" }}>E-Mail: {row.email}</MDTypography>
            </Grid>

            <Grid item sm={1}>
              <MDBox textAlign="right">
                <Icon
                  color="error"
                  fontSize="lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeRecipient(idx);
                  }}
                >
                  cancel
                </Icon>
              </MDBox>
            </Grid>
          </Grid>
        </Stack>
      </>
    );
  };

  const toggleOption = (array, fieldName, option) => {
    if (array == null) {
      setValue(fieldName, [option], { shouldDirty: true });
    } else if ((array ?? []).includes(option)) {
      setValue(
        fieldName,
        array.filter((e) => {
          return e !== option;
        }),
        { shouldDirty: true }
      );
    } else {
      setValue(fieldName, [...array, option], { shouldDirty: true });
    }
  };

  return (
    <>
      <Grid item xs={12} sm={6}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography component="label" variant="body2">
            Audience
          </MDTypography>
        </MDBox>
        <Controller
          name="audience"
          control={control}
          render={({ field }) => {
            const checkboxMapped = {};
            (field.value ?? []).forEach((element) => {
              checkboxMapped[element] = true;
            });
            return (
              <Grid container direction="column">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={!!checkboxMapped.New}
                    onChange={(e) => toggleOption(field.value, "audience", "New")}
                  />
                  <MDTypography variant="text1">New</MDTypography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={!!checkboxMapped.ATC}
                    onChange={(e) => toggleOption(field.value, "audience", "ATC")}
                  />
                  <MDTypography variant="text1">ATC</MDTypography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={!!checkboxMapped.Screened}
                    onChange={(e) => toggleOption(field.value, "audience", "Screened")}
                  />
                  <MDTypography variant="text1">Screened</MDTypography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={!!checkboxMapped["Pre-Hire"]}
                    onChange={(e) => toggleOption(field.value, "audience", "Pre-Hire")}
                  />
                  <MDTypography variant="text1">Pre-Hire</MDTypography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={!!checkboxMapped.Declined}
                    onChange={(e) => toggleOption(field.value, "audience", "Declined")}
                  />
                  <MDTypography variant="text1">Declined</MDTypography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={!!checkboxMapped.Employee}
                    onChange={(e) => toggleOption(field.value, "audience", "Employee")}
                  />
                  <MDTypography variant="text1">Employee</MDTypography>
                </Stack>
              </Grid>
            );
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Grid container>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <MDTypography variant="body2">Notify Job Recipients</MDTypography>
              <Controller
                name="notifyJobRecipients"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value === "Yes"}
                    disabled={!watch("jobSlug")}
                    onChange={(e) => field.onChange(e.target.checked ? "Yes" : "No")}
                  />
                )}
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <MDTypography variant="body2">Notify Candiate</MDTypography>
              <Controller
                name="notifyCandidate"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value === "Yes"}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setValue("showResultsToCandidate", "No", { shouldDirty: true });
                        field.onChange("No");
                      } else {
                        field.onChange("Yes");
                      }
                    }}
                  />
                )}
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <MDTypography variant="body2">Show Results To Candidate</MDTypography>
              <Controller
                name="showResultsToCandidate"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value === "Yes"}
                    disabled={watch("notifyCandidate") !== "Yes"}
                    onChange={(e) => field.onChange(e.target.checked ? "Yes" : "No")}
                  />
                )}
              />
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <MDBox mt={1} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography component="label" variant="body2">
            Extra Notification Recipients
          </MDTypography>
          <IconButton color="info" onClick={addNotificationRecipients}>
            <AddCircleRounded />
          </IconButton>
        </MDBox>
        <Controller
          name="notificationRecipients"
          control={control}
          render={({ field }) => (
            <DataList
              renderRow={notificationRecipientsRenderRow}
              data={getValues().notificationRecipients === undefined ? [] : field.value}
              greybar
              divider
              scrollY
              tableHeight={170}
              height={200}
              isLoading={false}
            />
          )}
        />
      </Grid>
      <NotificationRecipientsModal
        open={notificationRecipientsModalOpen}
        setOpen={setNotificationRecipientsModalOpen}
        NotificationRecipientsArray={getValues().notificationRecipients || []}
        setValue={setValue}
        setReminderToSaveChangesModalOpen={() => {}}
      />
    </>
  );
};

export default SettingsStep;
